/**
 * Helpers for turning raw Stripe/edge-function checkout failures into
 * user-friendly states. The most common hard failure is a missing price
 * (lookup key not present in the target Stripe environment), which the
 * edge functions return as a 404 with `Price not found for lookup key '…'`.
 * Users must never see that raw 404 — we show a friendly page instead.
 */

export type CheckoutErrorKind = "price_not_found" | "generic";

const PRICE_NOT_FOUND_PATTERNS = [
  "price not found",
  "no such price",
  "resource_missing",
  "lookup key",
];

/** True when the failure means the configured Stripe price doesn't exist. */
export function isPriceNotFoundError(message?: string | null): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return PRICE_NOT_FOUND_PATTERNS.some((p) => m.includes(p));
}

export function classifyCheckoutError(message?: string | null): CheckoutErrorKind {
  return isPriceNotFoundError(message) ? "price_not_found" : "generic";
}

/**
 * Reads the real error message out of a `supabase.functions.invoke` result.
 * Non-2xx responses give a generic "non-2xx status code" message, so we pull
 * the JSON body off `error.context` when it's available.
 */
export async function readFunctionErrorMessage(
  error: unknown,
  data?: unknown,
): Promise<string | null> {
  const dataError = (data as { error?: string } | null | undefined)?.error;
  if (dataError) return dataError;

  const ctx = (error as { context?: Response } | null)?.context;
  if (ctx && typeof ctx === "object" && typeof (ctx as Response).clone === "function") {
    try {
      const body = await (ctx as Response).clone().json();
      if (body && typeof body.error === "string") return body.error;
    } catch {
      /* body wasn't JSON — fall through to the message below */
    }
  }

  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return null;
}

/** Friendly, non-technical copy for a checkout failure. */
export function checkoutErrorCopy(kind: CheckoutErrorKind) {
  if (kind === "price_not_found") {
    return {
      title: "This plan isn't available to buy right now",
      body: "Our payment provider can't find the pricing for this plan, so we've stopped checkout before you were charged. Nothing has been taken from your card. This is on our side, not yours — we'll get it fixed fast.",
    };
  }
  return {
    title: "We couldn't start checkout",
    body: "Something went wrong reaching our payment provider. You haven't been charged. Try again in a moment, or reach out and we'll sort it with you.",
  };
}

export const SUPPORT_EMAIL = "hello@coachaifit.com";

export function checkoutSupportMailto(details?: string) {
  const subject = encodeURIComponent("Checkout problem — Coach AI");
  const bodyLines = [
    "Hi Coach AI team,",
    "",
    "I hit a problem trying to subscribe.",
    details ? `Details: ${details}` : "",
  ].filter(Boolean);
  return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${encodeURIComponent(
    bodyLines.join("\n"),
  )}`;
}
